import React, { useState, useEffect } from 'react';
import { messagesAPI, teamAPI } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Plus, 
  Send,
  Inbox,
  Mail,
  MailOpen,
  User,
  Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { FeatureGate } from '../components/common/FeatureGate';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

export const Messages = () => {
  const { hasFeature, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [formData, setFormData] = useState({
    recipient_id: '',
    subject: '',
    content: '',
    is_internal: true,
  });

  useEffect(() => {
    if (hasFeature('messaging')) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [hasFeature]);

  const fetchData = async () => {
    try {
      const [messagesRes, teamRes] = await Promise.all([
        messagesAPI.getAll(),
        teamAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setMessages(messagesRes.data);
      // Filter out current user from team members
      const otherMembers = (teamRes.data || []).filter(m => m.user_id !== user?.user_id);
      setTeamMembers(otherMembers);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.recipient_id) {
      toast.error('Please select a recipient');
      return;
    }
    try {
      await messagesAPI.create(formData);
      toast.success('Message sent successfully');
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleMarkRead = async (messageId) => {
    try {
      await messagesAPI.markRead(messageId);
      fetchData();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const resetForm = () => {
    setFormData({ recipient_id: '', subject: '', content: '', is_internal: true });
    setIsDialogOpen(false);
  };

  const getTeamMemberName = (userId) => {
    if (userId === user?.user_id) return 'You';
    const member = teamMembers.find(m => m.user_id === userId);
    return member?.name || userId;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const inboxMessages = messages.filter(m => m.recipient_id === user?.user_id);
  const sentMessages = messages.filter(m => m.sender_id === user?.user_id);
  const unreadCount = inboxMessages.filter(m => !m.read_at).length;

  if (!hasFeature('messaging')) {
    return (
      <FeatureGate feature="messaging">
        <div className="h-[600px]" />
      </FeatureGate>
    );
  }

  if (loading) {
    return <PageLoader text="Loading messages..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="messages-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Team Messages</h1>
          <p className="text-slate-500 mt-1">Communicate with your team members</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="new-message-btn">
              <Plus className="h-4 w-4" />
              New Message
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>New Message</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="recipient_id">Send To *</Label>
                <Select
                  value={formData.recipient_id}
                  onValueChange={(value) => setFormData({ ...formData, recipient_id: value })}
                >
                  <SelectTrigger data-testid="recipient-select">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.length > 0 ? (
                      teamMembers.map((member) => (
                        <SelectItem key={member.user_id} value={member.user_id}>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                                {getInitials(member.name)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <span className="font-medium">{member.name}</span>
                              <span className="text-slate-400 ml-2 text-sm">({member.role})</span>
                            </div>
                          </div>
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-members" disabled>
                        No team members found
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {teamMembers.length === 0 && (
                  <p className="text-sm text-amber-600">
                    No other team members found. Invite team members from the Team page.
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  placeholder="Enter message subject"
                  data-testid="subject-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Message *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={5}
                  placeholder="Type your message..."
                  data-testid="content-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" className="gap-2" disabled={!formData.recipient_id} data-testid="send-message-btn">
                  <Send className="h-4 w-4" />
                  Send Message
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Members Quick View */}
      {teamMembers.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
              <Users className="h-4 w-4" />
              <span>Team Members</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {teamMembers.slice(0, 8).map((member) => (
                <div
                  key={member.user_id}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 cursor-pointer transition-colors"
                  onClick={() => {
                    setFormData({ ...formData, recipient_id: member.user_id });
                    setIsDialogOpen(true);
                  }}
                >
                  <Avatar className="h-5 w-5">
                    <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-slate-700">{member.name}</span>
                </div>
              ))}
              {teamMembers.length > 8 && (
                <div className="px-3 py-1.5 rounded-full bg-slate-100 text-sm text-slate-500">
                  +{teamMembers.length - 8} more
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Inbox */}
        <Card className="lg:col-span-2" data-testid="inbox-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Inbox className="h-5 w-5" />
              Inbox
              {unreadCount > 0 && (
                <Badge className="bg-blue-600">{unreadCount}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              {inboxMessages.length > 0 ? (
                <div className="space-y-2">
                  {inboxMessages.map((message) => (
                    <div
                      key={message.message_id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        message.read_at 
                          ? 'border-slate-100 hover:border-slate-200' 
                          : 'border-blue-200 bg-blue-50 hover:bg-blue-100'
                      }`}
                      onClick={() => {
                        setSelectedMessage(message);
                        if (!message.read_at) handleMarkRead(message.message_id);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {message.read_at ? (
                            <MailOpen className="h-5 w-5 text-slate-400 mt-0.5" />
                          ) : (
                            <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-5 w-5">
                                <AvatarFallback className="text-xs bg-slate-200">
                                  {getInitials(getTeamMemberName(message.sender_id))}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm text-slate-500">
                                {getTeamMemberName(message.sender_id)}
                              </span>
                            </div>
                            <h4 className={`font-medium mt-1 ${message.read_at ? 'text-slate-700' : 'text-slate-900'}`}>
                              {message.subject}
                            </h4>
                            <p className="text-sm text-slate-500 mt-1 line-clamp-1">{message.content}</p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
                          {formatRelativeTime(message.created_at)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Inbox className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p>No messages in inbox</p>
                  <p className="text-sm mt-1">Messages from team members will appear here</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Message Detail or Sent */}
        <Card data-testid="message-detail-card">
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedMessage ? 'Message' : 'Sent Messages'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedMessage ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Subject</p>
                  <p className="font-medium text-slate-900">{selectedMessage.subject}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">From</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                        {getInitials(getTeamMemberName(selectedMessage.sender_id))}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-slate-700">{getTeamMemberName(selectedMessage.sender_id)}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Date</p>
                  <p className="text-sm text-slate-700">{formatRelativeTime(selectedMessage.created_at)}</p>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedMessage.content}</p>
                </div>
                <Button variant="outline" className="w-full" onClick={() => setSelectedMessage(null)}>
                  Back to Inbox
                </Button>
              </div>
            ) : (
              <ScrollArea className="h-[400px]">
                {sentMessages.length > 0 ? (
                  <div className="space-y-2">
                    {sentMessages.map((message) => (
                      <div key={message.message_id} className="p-3 rounded-lg border border-slate-100">
                        <h4 className="font-medium text-slate-700 text-sm">{message.subject}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-slate-400">To:</span>
                          <Avatar className="h-4 w-4">
                            <AvatarFallback className="text-[10px] bg-slate-200">
                              {getInitials(getTeamMemberName(message.recipient_id))}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-slate-500">{getTeamMemberName(message.recipient_id)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Send className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">No sent messages</p>
                  </div>
                )}
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Messages;
